import type { Agent, AgentId, AgentsBoard, Proposal } from '../../api/agentSchemas';
import { AGENT_FORM, AGENT_SEED, STATUS_LABEL, STATUS_TONE } from './agentLook';
import { DotOrb } from './orb/DotOrb';

const MINI_ORB = 40;
const CARD_ORB = 64;

type ListsProps = {
  board: AgentsBoard;
  onOpen: (proposalId: string) => void;
};

/** "Para revisar": what the team prepared, oldest decision first, each signed by its agent. */
export function PendingProposals({ board, onOpen }: ListsProps) {
  const pending = board.proposals.filter((proposal) => proposal.status === 'pending');
  if (pending.length === 0) {
    return (
      <p className="row-sub agents-empty">
        Nada que revisar. Cuando tus números muestren algo que se pueda arreglar, tu equipo te lo deja listo aquí.
      </p>
    );
  }
  return (
    <ul className="group rows agents-proposals">
      {pending.map((proposal) => (
        <li key={proposal.id} className="row agents-proposal">
          <AgentMiniOrb agentId={proposal.agentId} />
          <div className="grow">
            <p className="row-title">{proposal.title}</p>
            <p className="row-sub">
              {agentName(board.agents, proposal.agentId)} · {proposal.reason}
            </p>
          </div>
          <button type="button" className="btn small" onClick={() => onOpen(proposal.id)}>
            Revisar
          </button>
        </li>
      ))}
    </ul>
  );
}

/** "Tu equipo": one card per agent, its state in words, what it is on and what it may do. */
export function TeamCards({ agents }: { agents: readonly Agent[] }) {
  return (
    <ul className="agents-team">
      {agents.map((agent) => (
        <li key={agent.id} className="agents-card" data-status={agent.status}>
          <div className="agents-card-head">
            <AgentMiniOrb agentId={agent.id} size={CARD_ORB} />
            <div>
              <p className="agents-card-name">{agent.name}</p>
              <p className="row-sub">{agent.title}</p>
            </div>
          </div>
          <p className="status" data-tone={STATUS_TONE[agent.status]}>
            {STATUS_LABEL[agent.status]}
          </p>
          <dl className="agents-facts">
            <dt>Hace</dt>
            <dd>{agent.role}</dd>
            {agent.task ? (
              <>
                <dt>Ahora</dt>
                <dd>{agent.task}</dd>
              </>
            ) : null}
            <dt>Permiso</dt>
            <dd>{agent.permission}</dd>
          </dl>
        </li>
      ))}
    </ul>
  );
}

/** "Hecho": what was approved or set aside, and what came of it. */
export function DoneProposals({ board, onOpen }: ListsProps) {
  const closed = board.proposals.filter((proposal) => proposal.status !== 'pending');
  if (closed.length === 0) return null;
  return (
    <ul className="group rows agents-done">
      {closed.map((proposal) => (
        <li key={proposal.id} className="row">
          <AgentMiniOrb agentId={proposal.agentId} />
          <div className="grow">
            <p className="row-title">{proposal.title}</p>
            <p className="row-sub">{closedLine(proposal)}</p>
          </div>
          <button type="button" className="btn small secondary" onClick={() => onOpen(proposal.id)}>
            Ver
          </button>
        </li>
      ))}
    </ul>
  );
}

/** Each agent's dot in a small dark tile: a window into the room where the team works. */
export function AgentMiniOrb({ agentId, size = MINI_ORB }: { agentId: AgentId; size?: number }) {
  return (
    <span className="agents-tile" style={{ width: size, height: size }}>
      <DotOrb size={size} form={AGENT_FORM[agentId]} seed={AGENT_SEED[agentId]} onDark />
    </span>
  );
}

function closedLine(proposal: Proposal): string {
  if (proposal.status === 'dismissed') return 'Descartada.';
  const parts = [proposal.result?.summary, proposal.result?.measured].filter(Boolean);
  return parts.length > 0 ? parts.join(' ') : 'Aprobada.';
}

export function agentName(agents: readonly Agent[], agentId: AgentId): string {
  return agents.find((agent) => agent.id === agentId)?.name ?? 'Tu equipo';
}
